"use client";

import { useEffect, useMemo, useState } from "react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import FiltersBar from "@/components/dashboard/FiltersBar";
import KpiCard from "@/components/dashboard/KpiCard";
import OverviewChart from "@/components/dashboard/OverviewChart";
import StudentTypeChart from "@/components/dashboard/StudentTypeChart";
import SummaryTable from "@/components/dashboard/SummaryTable";
import { DashboardRow } from "@/lib/types";
import {
  buildKpis,
  formatNumber,
  groupByStudentType,
  groupByTerm,
  percent
} from "@/lib/utils";

export default function HomePage() {
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedStudentType, setSelectedStudentType] = useState("all");

  useEffect(() => {
    async function loadData() {
      try {
        const response = await fetch("/api/dashboard");

        if (!response.ok) {
          const text = await response.text();
          console.error("API error:", response.status, text);
          return;
        }

        const contentType = response.headers.get("content-type") ?? "";
        if (!contentType.includes("application/json")) {
          const text = await response.text();
          console.error("Expected JSON but got:", text);
          return;
        }

        const data = await response.json();
        setRows(data);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const terms = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.term_name)));
  }, [rows]);

  const studentTypes = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.student_type_name)));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const termOk = selectedTerm === "all" || row.term_name === selectedTerm;
      const studentTypeOk =
        selectedStudentType === "all" ||
        row.student_type_name === selectedStudentType;
      return termOk && studentTypeOk;
    });
  }, [rows, selectedTerm, selectedStudentType]);

  const kpis = useMemo(() => buildKpis(filteredRows), [filteredRows]);
  const termChartData = useMemo(() => groupByTerm(filteredRows), [filteredRows]);
  const studentTypeChartData = useMemo(
    () => groupByStudentType(filteredRows),
    [filteredRows]
  );

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-6 py-10">
        <div className="mx-auto max-w-7xl">
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-6 py-10">
      <div className="mx-auto flex max-w-7xl flex-col gap-8">
        <DashboardHeader />

        <FiltersBar
          terms={terms}
          studentTypes={studentTypes}
          selectedTerm={selectedTerm}
          selectedStudentType={selectedStudentType}
          onTermChange={setSelectedTerm}
          onStudentTypeChange={setSelectedStudentType}
        />

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KpiCard
            title="Total Applications"
            value={formatNumber(kpis.totalApplications)}
          />
          <KpiCard
            title="Available Beds"
            value={formatNumber(kpis.totalBeds)}
          />
          <KpiCard
            title="Total Shortage"
            value={formatNumber(kpis.totalShortage)}
          />
          <KpiCard
            title="Waitlist"
            value={formatNumber(kpis.totalWaitlist)}
          />
          <KpiCard
            title="Occupancy Rate"
            value={percent(kpis.avgOccupancyRate)}
          />
        </section>

        <section className="grid gap-6 xl:grid-cols-2">
          <OverviewChart data={termChartData} />
          <StudentTypeChart data={studentTypeChartData} />
        </section>

        <SummaryTable rows={filteredRows} />
      </div>
    </main>
  );
}
